import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { Award, Download, X, Loader2, Share2, Image, FileText } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Check if device is mobile
const isMobileDevice = () => {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
};

const CertificatePreviewModal = ({ 
  open, 
  onClose, 
  enrollment, 
  courseName,
  userName 
}) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [certificateUrl, setCertificateUrl] = useState(null);
  const [certificateImageUrl, setCertificateImageUrl] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    setIsMobile(isMobileDevice());
  }, []);

  const handleGenerateCertificate = async () => {
    if (!enrollment?.id) return;
    
    setGenerating(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/api/certificates/course/${enrollment.id}`,
        { generate_image: true },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCertificateUrl(response.data.certificate_url);
      setCertificateImageUrl(response.data.image_url || null);
      toast.success(t('certificate.generated', 'Certificate generated successfully!'));
    } catch (error) {
      console.error('Certificate generation error:', error);
      toast.error(error.response?.data?.detail || t('certificate.generateError', 'Failed to generate certificate'));
    } finally {
      setGenerating(false);
    }
  };

  const downloadFile = async (url, filename, mimeType) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      const fullUrl = url.startsWith('http') ? url : `${API_URL}${url}`;
      
      const response = await fetch(fullUrl, {
        method: 'GET',
        headers: { 
          'Authorization': `Bearer ${token}`,
        }
      });
      
      if (!response.ok) {
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('text/html')) {
          throw new Error('Session expired or file not found');
        }
        throw new Error(`Download failed: ${response.status}`);
      }
      
      const blob = await response.blob();
      
      if (blob.size === 0) {
        throw new Error('Downloaded file is empty');
      }
      
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = filename;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(blobUrl);
      }, 100);
      
      toast.success(t('certificate.downloaded', 'Certificate downloaded successfully!'));
    } catch (error) {
      console.error('Download error:', error);
      toast.error(error.message || t('certificate.downloadError', 'Failed to download certificate'));
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPDF = () => {
    if (!certificateUrl) return;
    const filename = `certificate_${courseName.replace(/\s+/g, '_')}.pdf`;
    downloadFile(certificateUrl, filename, 'application/pdf');
  };

  const handleDownloadImage = () => {
    if (!certificateImageUrl && !certificateUrl) return;
    const imageUrl = certificateImageUrl || certificateUrl.replace('.pdf', '.png');
    const filename = `certificate_${courseName.replace(/\s+/g, '_')}.png`;
    downloadFile(imageUrl, filename, 'image/png');
  };

  const handleShare = async () => {
    if (!certificateImageUrl && !certificateUrl) return;
    
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const imageUrl = certificateImageUrl || certificateUrl.replace('.pdf', '.png');
      const fullUrl = imageUrl.startsWith('http') ? imageUrl : `${API_URL}${imageUrl}`;
      
      const response = await fetch(fullUrl, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      const blob = await response.blob();
      const file = new File([blob], `certificate_${courseName.replace(/\s+/g, '_')}.png`, { type: 'image/png' });
      
      if (navigator.share && navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({
          title: `Certificate: ${courseName}`,
          text: `I just completed ${courseName}! 🎉`,
          files: [file]
        });
        toast.success('Certificate shared successfully!');
      } else {
        const shareText = encodeURIComponent(`I just completed the course "${courseName}"! 🎉🎓`);
        const whatsappUrl = `https://wa.me/?text=${shareText}`;
        window.open(whatsappUrl, '_blank');
        toast.info('Opening WhatsApp to share...');
      }
    } catch (error) {
      if (error.name === 'AbortError') {
        return;
      }
      console.error('Share error:', error);
      const shareText = encodeURIComponent(`I just completed the course "${courseName}"! 🎉🎓`);
      const whatsappUrl = `https://wa.me/?text=${shareText}`;
      window.open(whatsappUrl, '_blank');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setCertificateUrl(null);
    setCertificateImageUrl(null);
    onClose();
  };

  // Get preview image URL
  const previewImageUrl = certificateImageUrl 
    ? (certificateImageUrl.startsWith('http') ? certificateImageUrl : `${API_URL}${certificateImageUrl}`)
    : null;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Award className="w-5 h-5 text-amber-500" />
            {t('certificate.title', 'Course Completion Certificate')}
          </DialogTitle>
          <DialogDescription>
            {t('certificate.description', 'Generate and download your certificate of completion')}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Certificate Preview */}
          {previewImageUrl ? (
            <div className="rounded-lg overflow-hidden border-2 border-amber-200 dark:border-amber-800 shadow-lg">
              <img 
                src={previewImageUrl} 
                alt="Certificate Preview" 
                className="w-full h-auto"
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
            </div>
          ) : (
            <div className="bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-950/20 dark:to-orange-950/20 rounded-lg p-6 border-2 border-amber-200 dark:border-amber-800">
              <div className="text-center space-y-4">
                <Award className="w-16 h-16 text-amber-500 mx-auto" />
                <div>
                  <p className="text-sm text-muted-foreground uppercase tracking-wider">
                    {t('certificate.certifies', 'This certifies that')}
                  </p>
                  <h3 className="text-2xl font-bold text-amber-700 dark:text-amber-400 mt-2">
                    {userName}
                  </h3>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">
                    {t('certificate.hasCompleted', 'has successfully completed')}
                  </p>
                  <h4 className="text-xl font-semibold mt-1">{courseName}</h4>
                </div>
                <p className="text-sm text-muted-foreground">
                  {new Date().toLocaleDateString('en-US', { 
                    year: 'numeric', 
                    month: 'long', 
                    day: 'numeric' 
                  })}
                </p>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          {!certificateUrl ? (
            <div className="text-center">
              <Button 
                onClick={handleGenerateCertificate} 
                disabled={generating}
                className="bg-amber-600 hover:bg-amber-700"
              >
                {generating ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    {t('certificate.generating', 'Generating...')}
                  </>
                ) : (
                  <>
                    <Award className="w-4 h-4 mr-2" />
                    {t('certificate.generate', 'Generate Certificate')}
                  </>
                )}
              </Button>
            </div>
          ) : (
            <div className="flex flex-wrap items-center justify-center gap-3">
              {/* Download Dropdown */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button 
                    className="bg-green-600 hover:bg-green-700" 
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Processing...
                      </>
                    ) : (
                      <>
                        <Download className="w-4 h-4 mr-2" />
                        Download
                      </>
                    )}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent>
                  <DropdownMenuItem onClick={handleDownloadPDF}>
                    <FileText className="w-4 h-4 mr-2" />
                    Download as PDF
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={handleDownloadImage}>
                    <Image className="w-4 h-4 mr-2" />
                    Download as Image
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              {/* Share Button (Mobile) */}
              {isMobile && (
                <Button 
                  variant="outline"
                  onClick={handleShare}
                  disabled={loading}
                  className="border-green-500 text-green-600 hover:bg-green-50"
                >
                  <Share2 className="w-4 h-4 mr-2" />
                  Share
                </Button>
              )}
            </div>
          )}
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={handleClose}>
            <X className="w-4 h-4 mr-2" />
            {t('common.close', 'Close')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CertificatePreviewModal;
